import { createMemo, createSignal, Show, onCleanup } from "solid-js";

interface Props {
  data: number[];
  width?: number;
  height?: number;
  yMax?: number;
  color?: string;
  fill?: string;
  label?: string;
  samplesPerSecond?: number;
    capacity?: number;
    fillWidth?: boolean;
  showYMax?: boolean;
  hideAxisLabel?: boolean;
  animated?: boolean;
}

export default function Sparkline(props: Props) {
  const w = () => props.width ?? 280;
  const h = () => props.height ?? 40;
  const color = () => props.color ?? "var(--accent-purple, #7c4dff)";
  const fill = () => props.fill;
  const animated = () => props.animated !== false;

  const extremes = createMemo(() => {
    const d = props.data;
    if (d.length === 0) return { min: 0, max: 1 };
    let min = Infinity, max = -Infinity;
    for (const v of d) {
      if (v < min) min = v;
      if (v > max) max = v;
    }
    min = 0;
    const yMax = props.yMax ?? Math.max(1, max);
    if (max < yMax) max = yMax;
    return { min, max };
  });

  const computed = createMemo(() => {
    const d = props.data;
    if (d.length === 0) return { points: "" as string, coords: [] as { x: number; y: number; v: number }[] };
    const { min, max } = extremes();
    const range = max - min || 1;
    const W = w();
    const H = h();
    const n = d.length;
    const cap = props.capacity && props.capacity > 1 ? props.capacity : n;
    const step = cap > 1 ? W / (cap - 1) : 0;
    const startX = W - (n - 1) * step;
    const coords: { x: number; y: number; v: number }[] = [];
    let out = "";
    for (let i = 0; i < n; i++) {
      const x = startX + i * step;
      const y = H - ((d[i] - min) / range) * H;
      coords.push({ x, y, v: d[i] });
      out += `${i === 0 ? "" : " "}${x.toFixed(1)},${y.toFixed(1)}`;
    }
    return { points: out, coords };
  });

  const [displayedPoints, setDisplayedPoints] = createSignal("");
  let prevPoints = "";
  let raf = 0;

  function lerpPaths(a: string, b: string, t: number): string {
    if (!a || !b) return b;
    const A = a.split(/\s+/);
    const B = b.split(/\s+/);
    if (A.length !== B.length) return b;
    let out = "";
    for (let i = 0; i < B.length; i++) {
      const [ax, ay] = A[i].split(",").map(Number);
      const [bx, by] = B[i].split(",").map(Number);
      const x = ax + (bx - ax) * t;
      const y = ay + (by - ay) * t;
      out += `${i === 0 ? "" : " "}${x.toFixed(1)},${y.toFixed(1)}`;
    }
    return out;
  }

  createMemo(() => {
    const target = computed().points;
    if (!animated() || !prevPoints) {
      setDisplayedPoints(target);
      prevPoints = target;
      return;
    }
    const from = prevPoints;
    const to = target;
    const start = performance.now();
    const dur = 220;
    cancelAnimationFrame(raf);
    const tick = (now: number) => {
      const t = Math.min(1, (now - start) / dur);
      const eased = 1 - Math.pow(1 - t, 3);
      setDisplayedPoints(lerpPaths(from, to, eased));
      if (t < 1) raf = requestAnimationFrame(tick);
      else prevPoints = to;
    };
    raf = requestAnimationFrame(tick);
  });

  onCleanup(() => cancelAnimationFrame(raf));

  const firstX = createMemo(() => {
    const n = props.data.length;
    if (n === 0) return 0;
    const cap = props.capacity && props.capacity > 1 ? props.capacity : n;
    const step = cap > 1 ? w() / (cap - 1) : 0;
    return w() - (n - 1) * step;
  });

  const areaPoints = createMemo(() => {
    const p = displayedPoints();
    if (!p || !fill()) return "";
    return `${firstX().toFixed(1)},${h()} ${p} ${w()},${h()}`;
  });

  const lastValue = () => {
    const d = props.data;
    return d.length ? d[d.length - 1] : null;
  };

  const [hover, setHover] = createSignal<{ x: number; y: number; v: number } | null>(null);
  let svgRef: SVGSVGElement | undefined;

  function onMove(e: MouseEvent) {
    if (!svgRef) return;
    const rect = svgRef.getBoundingClientRect();
    const px = (e.clientX - rect.left) * (w() / rect.width);
    const cs = computed().coords;
    if (!cs.length) return;
    let best = cs[0], bestDx = Math.abs(cs[0].x - px);
    for (let i = 1; i < cs.length; i++) {
      const dx = Math.abs(cs[i].x - px);
      if (dx < bestDx) { best = cs[i]; bestDx = dx; }
    }
    setHover(best);
  }

  return (
    <div style={{
      position: "relative",
      width: props.fillWidth ? "100%" : `${w()}px`,
      height: `${h()}px`,
    }}>
      <svg
        ref={svgRef}
        width={props.fillWidth ? "100%" : w()}
        height={h()}
        viewBox={`0 0 ${w()} ${h()}`}
        preserveAspectRatio="none"
        style={{ display: "block" }}
        onMouseMove={onMove}
        onMouseLeave={() => setHover(null)}
      >
        <Show when={fill()}>
          <polygon points={areaPoints()} fill={fill()!} opacity="0.35" />
        </Show>
        <polyline
          points={displayedPoints() || computed().points}
          fill="none"
          stroke={color()}
          stroke-width="1.5"
          stroke-linejoin="round"
          stroke-linecap="round"
        />
        <Show when={hover()}>
          {(pt) => (
            <>
              <line x1={pt().x} x2={pt().x} y1={0} y2={h()} stroke="var(--dark-text-muted)" stroke-width="0.6" stroke-dasharray="2 3" opacity="0.7" />
              <circle cx={pt().x} cy={pt().y} r={2.5} fill={color()} />
            </>
          )}
        </Show>
      </svg>
      <Show when={hover()}>
        {(pt) => (
          <span style={{
            position: "absolute",
            top: "-18px",
            left: `${(pt().x / w()) * 100}%`,
            transform: "translateX(-50%)",
            "font-size": "10px",
            "font-family": "var(--font-family-mono)",
            color: "var(--dark-text-primary)",
            background: "var(--dark-bg-elevated)",
            border: "1px solid var(--dark-border)",
            padding: "1px 6px",
            "border-radius": "var(--radius-sm)",
            "white-space": "nowrap",
            "pointer-events": "none",
            "z-index": "2",
          }}>
            {Math.round(pt().v)}
          </span>
        )}
      </Show>
      <Show when={props.showYMax && props.data.length > 0}>
        <span style={{
          position: "absolute",
          top: "2px",
          left: "4px",
          "font-size": "10px",
          "font-family": "monospace",
          color: "var(--dark-text-muted)",
          "pointer-events": "none",
        }}>
          {Math.round(extremes().max)}
        </span>
      </Show>
      <Show when={props.label || lastValue() !== null}>
        <span style={{
          position: "absolute",
          top: "2px",
          right: "4px",
          "font-size": "10px",
          "font-family": "monospace",
          color: "var(--dark-text-muted)",
          "pointer-events": "none",
        }}>
          {props.label ?? `${Math.round(lastValue()!)}`}
        </span>
      </Show>
      <Show when={!props.hideAxisLabel && props.data.length > 0}>
        <span style={{
          position: "absolute",
          bottom: "0",
          left: "2px",
          "font-size": "9px",
          "font-family": "monospace",
          color: "var(--dark-text-muted)",
          opacity: "0.6",
          "pointer-events": "none",
        }}>
          {axisLabel(props.data.length, props.samplesPerSecond ?? 1)}
        </span>
      </Show>
    </div>
  );
}

function axisLabel(points: number, samplesPerSecond: number): string {
  const seconds = points / samplesPerSecond;
  if (seconds < 60) return `${Math.round(seconds)}s`;
  if (seconds < 3600) return `${Math.round(seconds / 60)}m`;
  return `${(seconds / 3600).toFixed(1)}h`;
}
